import React, { useMemo } from "react";
import { useField, useFormikContext } from "formik";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { muiInputSx } from "../../lib/constants/mui-input-sx";

const SelectFields = ({
  label,
  required = false,
  options = [],
  name,
  onScrollBottom,
  ...props
}) => {
  const { t } = useTranslation();
  const [field, meta] = useField(name);
  const { touched, setFieldValue, setFieldTouched } = useFormikContext();
  const selectedOption = options.find((option) => option.value === field.value) || null;
  const hasError = touched[name] && Boolean(meta.error);
  const inputSx = useMemo(() => muiInputSx(hasError), [hasError]);

  return (
    <div className="flex-1">
      <label htmlFor={name} className="block mb-2 text-base text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <Autocomplete
        id={name}
        options={options}
        getOptionLabel={(option) => option.label || ""}
        value={selectedOption}
        onChange={async (_, newValue) => {
          const value = newValue ? newValue.value : "";
          await setFieldValue(name, value, true);
          setFieldTouched(name, true, false);
          if (props.onChange) {
            props.onChange({ target: { value } });
          }
        }}
        onBlur={() => {
          setFieldTouched(name, true, false);
        }}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        renderOption={props.renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            placeholder={t("selectAnOption")}
            error={hasError}
            helperText={hasError ? meta.error : ""}
            InputLabelProps={{ shrink: true }}
            disabled={props.disabled}
            sx={inputSx}
          />
        )}
        disabled={props.disabled}
        sx={{
          "& .MuiAutocomplete-listbox": {
            maxHeight: props.maxHeight || 200,
          },
        }}
        clearOnEscape
        ListboxProps={{
          onScroll: (event) => {
            const listboxNode = event.currentTarget;
            if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 50) {
              onScrollBottom && onScrollBottom();
            }
          },
        }}
      />
    </div>
  );
};

export default SelectFields;
